/**
 * Modern Cart Manager for Shopify
 * Uses latest Shopify Ajax Cart API (2024/2025)
 * Supports locale-aware URLs and modern async/await
 */
class ShopifyCartManager {
  constructor() {
    // Use locale-aware base URL
    this.baseUrl = window.Shopify?.routes?.root || '/';
    this.init();
  }

  init() {
    this.updateCartCount();
    this.setupEventListeners();
  }

  /**
   * Get base URL for cart operations (locale-aware)
   */
  getCartUrl(endpoint) {
    return `${this.baseUrl}cart/${endpoint}`;
  }

  /**
   * Add single item to cart
   * @param {number} variantId - Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @param {object} properties - Optional line item properties
   * @returns {Promise<object>} Cart response
   */
  async addItem(variantId, quantity = 1, properties = {}) {
    try {
      const response = await fetch(this.getCartUrl('add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: properties
          }]
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || error.message || 'Failed to add to cart');
      }

      const data = await response.json();
      await this.updateCartCount();

      // Trigger cart updated event
      this.triggerCartUpdate(data);

      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart line item quantity
   * @param {number} line - Line item index (1-based)
   * @param {number} quantity - New quantity (0 to remove)
   * @returns {Promise<object>} Updated cart
   */
  async updateLineItem(line, quantity) {
    try {
      const response = await fetch(this.getCartUrl('change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || error.message || 'Failed to update cart');
      }

      const cart = await response.json();
      await this.updateCartCount();
      this.triggerCartUpdate(cart);

      return cart;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Get current cart data
   * @returns {Promise<object>} Cart object
   */
  async getCart() {
    try {
      const response = await fetch(this.getCartUrl('.js'), {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Update cart count in header/UI
   */
  async updateCartCount() {
    try {
      const cart = await this.getCart();

      // Update all elements with data-cart-count attribute
      document.querySelectorAll('[data-cart-count]').forEach(element => {
        element.textContent = cart.item_count;

        // Show/hide based on count
        if (cart.item_count > 0) {
          element.style.display = '';
          element.classList.remove('hidden');
        } else {
          element.style.display = 'none';
          element.classList.add('hidden');
        }
      });

      // Update elements with class .cart-count
      document.querySelectorAll('.cart-count').forEach(element => {
        element.textContent = cart.item_count;
      });

      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Trigger cart updated event
   * @param {object} cart - Cart data
   */
  triggerCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart },
      bubbles: true
    }));
  }

  /**
   * Setup event listeners for cart interactions
   */
  setupEventListeners() {
    // Listen for cart icon clicks
    document.addEventListener('click', (e) => {
      const cartTrigger = e.target.closest('[data-cart-trigger]');
      if (cartTrigger) {
        e.preventDefault();
        this.openCartDrawer();
      }
    });
  }

  /**
   * Open cart drawer (if exists)
   */
  openCartDrawer() {
    // Try different methods to open cart drawer
    const cartDrawer = document.querySelector('[data-cart-drawer]');

    if (cartDrawer) {
      cartDrawer.classList.add('is-open', 'active', 'open');
      document.body.style.overflow = 'hidden';

      // Trigger custom event
      document.dispatchEvent(new CustomEvent('cart:open'));
    } else {
      // Fallback to cart page
      console.error('Could not find cart drawer, redirecting to cart page.');
      window.location.href = `${this.baseUrl}cart`;
    }
  }

  /**
   * Close cart drawer
   */
  closeCartDrawer() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');

    if (cartDrawer) {
      cartDrawer.classList.remove('is-open', 'active', 'open');
      document.body.style.overflow = '';

      // Trigger custom event
      document.dispatchEvent(new CustomEvent('cart:close'));
    }
  }
}

// Initialize cart manager
const cartManager = new ShopifyCartManager();

// Make globally available
window.cartManager = cartManager;

console.log('🛒 Shopify Cart Manager initialized');